import React, { useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Page from "../components/Page";
import {
  IconReceipt,
  IconCalendar,
  IconFilter,
  IconSearch,
  IconUser,
  IconUserCheck,
  IconCurrencyDollar,
  IconCoins,
  IconChefHat,
  IconX,
  IconArmchair,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import { useKOTReport } from "../controllers/reports.controller";
import { useStoreSettings } from "../controllers/settings.controller";
import ReportExportWrapper from "../components/ReportExportWrapper";

export default function KOTReportPage() {
  const { t } = useTranslation();

  const filterTypeRef = useRef();
  const fromDateRef = useRef();
  const toDateRef = useRef();

  const [state, setState] = useState({
    filter: "today",
    fromDate: null,
    toDate: null,
    search: "",
    selectedWaiter: "all",
  });

  const { data: storeSettings } = useStoreSettings();
  const currency = storeSettings?.currency || "NPR";

  const { data, isLoading, error } = useKOTReport({
    dateFilter: state.filter,
    from: state.fromDate,
    to: state.toDate,
  });

  const filters = [
    { key: "today", value: "Today" },
    { key: "yesterday", value: "Yesterday" },
    { key: "last_7days", value: "Last 7 Days" },
    { key: "this_month", value: "This Month" },
    { key: "last_month", value: "Last Month" },
    { key: "custom", value: "Custom Date" },
  ];

  const rawReport = data?.report || [];
  const waiterSummary = data?.waiterSummary || [];

  // Filtered dataset based on selected waiter and search query
  const filteredReport = useMemo(() => {
    let result = rawReport;

    if (state.selectedWaiter && state.selectedWaiter !== "all") {
      result = result.filter(
        (item) =>
          (item.created_by &&
            item.created_by.toLowerCase() === state.selectedWaiter.toLowerCase()) ||
          (item.waiter_name &&
            item.waiter_name.toLowerCase() === state.selectedWaiter.toLowerCase())
      );
    }

    if (state.search) {
      const s = state.search.toLowerCase();
      result = result.filter(
        (item) =>
          item.token_no?.toString().includes(s) ||
          item.created_by?.toLowerCase().includes(s) ||
          item.waiter_name?.toLowerCase().includes(s) ||
          item.table_title?.toLowerCase().includes(s) ||
          item.item_name?.toLowerCase().includes(s)
      );
    }

    return result;
  }, [rawReport, state.selectedWaiter, state.search]);

  // Aggregated metrics for current filtered view
  const metrics = useMemo(() => {
    const orderIds = new Set();
    let totalItems = 0;
    let totalValuation = 0;
    let cancelledItems = 0;
    let cancelledValuation = 0;

    filteredReport.forEach((item) => {
      orderIds.add(item.order_id);
      const qty = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;
      const total = Number(item.item_total) || qty * price;

      totalItems += qty;
      if (item.status === "cancelled") {
        cancelledItems += qty;
        cancelledValuation += total;
      } else {
        totalValuation += total;
      }
    });

    return {
      totalKOTs: orderIds.size,
      totalItems,
      totalValuation,
      cancelledItems,
      cancelledValuation,
    };
  }, [filteredReport]);

  if (isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center h-full">
          <span className="loading loading-spinner loading-lg text-restro-green"></span>
        </div>
      </Page>
    );
  }

  if (error) return <Page>Error loading report.</Page>;

  const activeWaiterObj = waiterSummary.find(
    (w) =>
      w.waiter_key.toLowerCase() === state.selectedWaiter.toLowerCase() ||
      w.waiter_name.toLowerCase() === state.selectedWaiter.toLowerCase()
  );

  const columns = [
    { header: "Token No", key: "token_no" },
    { header: "Date", key: "date" },
    { header: "Waiter", key: "waiter" },
    { header: "Table", key: "table_title" },
    { header: "Item", key: "item" },
    { header: "Rate", key: "price" },
    { header: "Valuation", key: "item_total" },
    { header: "Status", key: "status" },
  ];

  const tableData = filteredReport.map((item) => ({
    token_no: item.token_no || "-",
    date: new Date(item.date).toLocaleString(),
    waiter: `${item.waiter_name || item.created_by || "System"} (${item.waiter_designation || "Staff"})`,
    table_title: item.table_title || "Walk-in",
    item: `${item.item_name} x${item.quantity}`,
    price: `${currency} ${(Number(item.price) || 0).toFixed(2)}`,
    item_total: `${currency} ${(Number(item.item_total) || 0).toFixed(2)}`,
    status: item.status,
  }));

  const summaryData = {
    "Waiter Filter":
      state.selectedWaiter !== "all"
        ? `${activeWaiterObj?.waiter_name || state.selectedWaiter} (${activeWaiterObj?.waiter_designation || "Staff"})`
        : "All Waiters",
    "Total KOTs Taken": metrics.totalKOTs,
    "Total Items Count": metrics.totalItems,
    "Total KOT Valuation": `${currency} ${metrics.totalValuation.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`,
    "Cancelled Items": `${metrics.cancelledItems} (${currency} ${metrics.cancelledValuation.toFixed(2)})`,
  };

  return (
    <Page>
      <ReportExportWrapper
        title="Kitchen Order Ticket (KOT) Report"
        reportType="Operational Reports"
        dateRange={filters.find((f) => f.key === state.filter)?.value}
        filters={`Waiter: ${
          state.selectedWaiter !== "all"
            ? activeWaiterObj?.waiter_name || state.selectedWaiter
            : "All Waiters"
        } | Search: ${state.search || "None"}`}
        columns={columns}
        tableData={tableData}
        summaryData={summaryData}
      >
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4 print:hidden">
          <div>
            <h3 className="text-3xl font-black tracking-tight flex items-center gap-2 text-blue-600">
              <IconReceipt size={32} /> KOT Report
            </h3>
            <p className="text-gray-500 text-sm mt-1">
              Operational tracking of kitchen orders, waiter counts, and item valuation
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => document.getElementById("filter-dialog").showModal()}
              className="btn btn-ghost bg-restro-gray border border-restro-border-green rounded-2xl gap-2 font-bold text-sm"
            >
              <IconFilter stroke={iconStroke} size={18} /> {t("reports.filters_button")}
            </button>
          </div>
        </div>

        {/* Dynamic KPI Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-restro-gray p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center shrink-0">
              <IconReceipt size={24} />
            </div>
            <div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Total KOTs Taken
              </div>
              <div className="text-2xl font-black text-gray-900 dark:text-white mt-0.5">
                {metrics.totalKOTs}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                {state.selectedWaiter === "all" ? "Across all staff" : "By selected waiter"}
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-restro-gray p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 flex items-center justify-center shrink-0">
              <IconCoins size={24} />
            </div>
            <div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Total KOT Valuation
              </div>
              <div className="text-2xl font-black text-emerald-600 mt-0.5">
                {currency} {metrics.totalValuation.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                Value of active KOT items
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-restro-gray p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-600 flex items-center justify-center shrink-0">
              <IconChefHat size={24} />
            </div>
            <div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Total Items Served
              </div>
              <div className="text-2xl font-black text-gray-900 dark:text-white mt-0.5">
                {metrics.totalItems}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                Ordered food & beverage items
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-restro-gray p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-500 flex items-center justify-center shrink-0">
              <IconAlertTriangle size={24} />
            </div>
            <div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Cancelled Items
              </div>
              <div className="text-2xl font-black text-red-500 mt-0.5">
                {metrics.cancelledItems}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                {currency} {metrics.cancelledValuation.toFixed(2)} void value
              </div>
            </div>
          </div>
        </div>

        {/* Waiter Summary Cards Section */}
        {waiterSummary.length > 0 && (
          <div className="mb-6 bg-white dark:bg-restro-gray p-5 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm print:hidden">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <IconUserCheck size={20} className="text-blue-600" />
                <h4 className="font-bold text-base text-gray-800 dark:text-gray-100">
                  Waiter Performance Breakdown
                </h4>
                <span className="text-xs text-gray-400">
                  (Click any waiter to filter the report)
                </span>
              </div>
              {state.selectedWaiter !== "all" && (
                <button
                  onClick={() => setState({ ...state, selectedWaiter: "all" })}
                  className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1"
                >
                  <IconX size={14} /> Clear Waiter Filter
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {waiterSummary.map((w) => {
                const isSelected =
                  state.selectedWaiter.toLowerCase() === w.waiter_key.toLowerCase() ||
                  state.selectedWaiter.toLowerCase() === w.waiter_name.toLowerCase();

                return (
                  <div
                    key={w.waiter_key}
                    onClick={() =>
                      setState({
                        ...state,
                        selectedWaiter: isSelected ? "all" : w.waiter_key,
                      })
                    }
                    className={`cursor-pointer p-4 rounded-2xl border transition-all duration-200 ${
                      isSelected
                        ? "bg-blue-50/70 border-blue-500 shadow-md ring-2 ring-blue-400/30 dark:bg-blue-950/40"
                        : "bg-gray-50/70 dark:bg-gray-800/50 border-gray-200/70 dark:border-gray-700/60 hover:border-blue-300 hover:shadow-sm"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black uppercase shrink-0 ${
                            isSelected
                              ? "bg-blue-600 text-white"
                              : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {w.waiter_name ? w.waiter_name[0] : "W"}
                        </div>
                        <div className="truncate">
                          <div className="font-bold text-sm text-gray-900 dark:text-white truncate">
                            {w.waiter_name}
                          </div>
                          <div className="text-[11px] text-gray-400 truncate">
                            {w.waiter_designation || "Staff"}
                          </div>
                        </div>
                      </div>
                      {isSelected && (
                        <span className="badge badge-primary badge-xs shrink-0">Selected</span>
                      )}
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-200/50 dark:border-gray-700/50 flex items-center justify-between text-xs">
                      <div>
                        <span className="text-gray-400">KOTs: </span>
                        <span className="font-black text-gray-800 dark:text-gray-200">
                          {w.total_kots}
                        </span>
                        <span className="text-gray-400 text-[10px]"> ({w.total_items} items)</span>
                      </div>
                      <div className="font-black text-emerald-600 text-xs">
                        {currency} {Number(w.total_valuation || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Filter & Search Bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6 print:hidden">
          {/* Waiter Select Dropdown */}
          <div className="sm:w-64">
            <select
              value={state.selectedWaiter}
              onChange={(e) => setState({ ...state, selectedWaiter: e.target.value })}
              className="select select-bordered w-full rounded-2xl bg-white dark:bg-restro-gray border-gray-200 dark:border-gray-700 font-medium text-sm"
            >
              <option value="all">All Waiters ({waiterSummary.length})</option>
              {waiterSummary.map((w) => (
                <option key={w.waiter_key} value={w.waiter_key}>
                  {w.waiter_name} ({w.total_kots} KOTs - {currency} {Number(w.total_valuation || 0).toFixed(0)})
                </option>
              ))}
            </select>
          </div>

          {/* Search Box */}
          <div className="flex-1 flex bg-white dark:bg-restro-gray px-3 py-1 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm items-center">
            <div className="text-gray-400 mr-2">
              <IconSearch size={18} />
            </div>
            <input
              type="text"
              placeholder="Search by token, waiter, table, or item name..."
              className="flex-1 bg-transparent border-none focus:ring-0 text-sm py-2 outline-none"
              value={state.search}
              onChange={(e) => setState({ ...state, search: e.target.value })}
            />
            {state.search && (
              <button
                onClick={() => setState({ ...state, search: "" })}
                className="text-gray-400 hover:text-gray-600 text-xs p-1"
              >
                <IconX size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Itemized KOT Records Table */}
        <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-[2rem] overflow-hidden shadow-sm">
          <div className="overflow-x-auto print:overflow-visible">
            <table className="table table-zebra w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 print:bg-transparent border-b border-gray-200 dark:border-gray-800">
                <tr>
                  <th className="font-bold text-xs uppercase tracking-wider p-4 text-gray-400 print:text-black">Token</th>
                  <th className="font-bold text-xs uppercase tracking-wider p-4 text-gray-400 print:text-black">Date / Time</th>
                  <th className="font-bold text-xs uppercase tracking-wider p-4 text-gray-400 print:text-black">Waiter / Staff</th>
                  <th className="font-bold text-xs uppercase tracking-wider p-4 text-gray-400 print:text-black">Table / Room</th>
                  <th className="font-bold text-xs uppercase tracking-wider p-4 text-gray-400 print:text-black">Item & Qty</th>
                  <th className="font-bold text-xs uppercase tracking-wider p-4 text-gray-400 print:text-black text-right">Rate</th>
                  <th className="font-bold text-xs uppercase tracking-wider p-4 text-gray-400 print:text-black text-right">Valuation</th>
                  <th className="font-bold text-xs uppercase tracking-wider p-4 text-gray-400 print:text-black text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredReport.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-20 opacity-50 italic">
                      No KOT records found for the selected filter or search criteria.
                    </td>
                  </tr>
                )}
                {filteredReport.map((item, i) => {
                  const qty = Number(item.quantity) || 0;
                  const price = Number(item.price) || 0;
                  const itemTotal = Number(item.item_total) || qty * price;
                  const isCancelled = item.status === "cancelled";

                  return (
                    <tr
                      key={i}
                      className={`hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition print:hover:bg-transparent ${
                        isCancelled ? "opacity-60 bg-red-50/20" : ""
                      }`}
                    >
                      <td className="p-4 font-bold text-gray-800 dark:text-gray-200">
                        #{item.token_no || "-"}
                      </td>
                      <td className="p-4 text-xs text-gray-500 print:text-black">
                        {new Date(item.date).toLocaleString()}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 flex items-center justify-center text-xs font-black uppercase shrink-0 print:hidden">
                            {(item.waiter_name || item.created_by || "S")[0]}
                          </div>
                          <div>
                            <div className="font-bold text-sm text-gray-900 dark:text-gray-100">
                              {item.waiter_name || item.created_by || "System"}
                            </div>
                            <div className="text-[11px] text-gray-400">
                              {item.waiter_designation ? (
                                <span className="badge badge-ghost badge-xs text-[10px] mr-1">
                                  {item.waiter_designation}
                                </span>
                              ) : null}
                              {item.created_by}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 font-medium text-gray-700 dark:text-gray-300">
                        {item.table_title || "Walk-in"}
                      </td>
                      <td className="p-4">
                        <span className="font-semibold text-gray-800 dark:text-gray-200">
                          {item.item_name}
                        </span>{" "}
                        <span className="badge badge-sm font-black ml-1">x{item.quantity}</span>
                        {item.notes && (
                          <div className="text-[11px] text-amber-600 italic">
                            Note: {item.notes}
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-right text-sm font-medium text-gray-600 dark:text-gray-300">
                        {currency} {price.toFixed(2)}
                      </td>
                      <td className="p-4 text-right text-sm font-bold text-gray-900 dark:text-white">
                        {currency} {itemTotal.toFixed(2)}
                      </td>
                      <td className="p-4 text-center">
                        <span
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                            isCancelled
                              ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                              : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {filteredReport.length > 0 && (
                <tfoot className="bg-gray-50 dark:bg-gray-900/60 font-bold border-t-2 border-gray-200 dark:border-gray-700">
                  <tr>
                    <td colSpan="4" className="p-4 text-xs uppercase tracking-wider text-gray-500">
                      Total ({metrics.totalKOTs} KOTs taken)
                    </td>
                    <td className="p-4 text-sm font-black">
                      {metrics.totalItems} Items
                    </td>
                    <td className="p-4 text-right text-xs uppercase text-gray-400">
                      Valuation Total:
                    </td>
                    <td className="p-4 text-right text-base font-black text-emerald-600">
                      {currency} {metrics.totalValuation.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="p-4"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

      {/* filter dialog */}
      <dialog id="filter-dialog" className="modal">
        <div className = 'modal-box border border-restro-border-green dark:rounded-3xl p-0 overflow-hidden'>
          <div className="bg-gray-900 p-8 text-white">
            <h3 className = "font-black text-2xl flex items-center gap-2">
                <IconFilter stroke={2} /> {t("reports.filter")}
            </h3>
            <p className="text-gray-400 text-sm mt-1">Select date range for report</p>
          </div>

          <div className="p-8 space-y-6">
            <div>
              <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">{t("reports.filter_label")}</label>
              <select
                className='select select-bordered w-full rounded-2xl bg-gray-50 dark:bg-gray-900 border-none'
                ref={filterTypeRef}
                defaultValue={state.filter}
              >
                {filters.map((filter, index) => (
                  <option key={index} value={filter.key}>
                    {filter.value}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-control">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">{t("reports.from")}</label>
                <input
                  type="date"
                  ref={fromDateRef}
                  className='bg-gray-50 dark:bg-gray-900 border-none rounded-2xl py-3 px-4 font-bold text-sm'
                />
              </div>
              <div className="form-control">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">{t("reports.to")}</label>
                <input
                  type="date"
                  ref={toDateRef}
                  className='bg-gray-50 dark:bg-gray-900 border-none rounded-2xl py-3 px-4 font-bold text-sm'
                />
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <button onClick={() => document.getElementById("filter-dialog").close()} className="flex-1 py-4 font-bold text-gray-500 bg-gray-100 dark:bg-gray-800 rounded-2xl transition active:scale-95">{t("reports.close")}</button>
              <button
                onClick={() => {
                  setState({
                    ...state,
                    filter: filterTypeRef.current.value,
                    fromDate: fromDateRef.current.value || null,
                    toDate: toDateRef.current.value || null,
                  });
                  document.getElementById("filter-dialog").close();
                }}
                className = 'flex-[1.5] py-4 font-bold text-white bg-restro-green rounded-2xl hover:bg-restro-green-button-hover transition active:scale-95 shadow-lg shadow-green-100 dark:shadow-none'
              >
                {t("reports.apply")}
              </button>
            </div>
          </div>
        </div>
      </dialog>
      </ReportExportWrapper>
    </Page>
  );
}
